<script setup>
import { computed, onMounted, ref, watch } from 'vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'alert',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  // Bold suffix appended to the description, e.g. the record name.
  descriptionValue: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  // When set, the user must type this value to enable the confirm button.
  confirmValue: {
    type: String,
    default: '',
  },
  confirmPlaceholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['confirm', 'close']);

const show = defineModel('show', { type: Boolean, default: false });

const dialogRef = ref(null);
const confirmInput = ref('');
let resolvePromise = null;

const confirmDisabled = computed(
  () =>
    Boolean(props.confirmValue) &&
    confirmInput.value.trim() !== props.confirmValue.trim()
);

watch(show, isVisible => {
  if (isVisible) dialogRef.value?.open();
  else dialogRef.value?.close();
});

onMounted(() => {
  if (show.value) dialogRef.value?.open();
});

// Promise-based API: `await confirmDialogRef.value.showConfirmation()`
// resolves true on confirm and false on cancel/dismiss.
const showConfirmation = () => {
  show.value = true;
  dialogRef.value?.open();
  return new Promise(resolve => {
    resolvePromise = resolve;
  });
};

const onConfirm = () => {
  if (confirmDisabled.value) return;
  emit('confirm');
  if (resolvePromise) {
    resolvePromise(true);
    resolvePromise = null;
    show.value = false;
  }
};

const onClose = () => {
  confirmInput.value = '';
  if (resolvePromise) {
    resolvePromise(false);
    resolvePromise = null;
  }
  if (show.value) {
    show.value = false;
    emit('close');
  }
};

defineExpose({ showConfirmation });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :type="type"
    :title="title"
    :description="description"
    :confirm-button-label="confirmButtonLabel"
    :cancel-button-label="cancelButtonLabel"
    :disable-confirm-button="confirmDisabled"
    @confirm="onConfirm"
    @close="onClose"
  >
    <template v-if="descriptionValue" #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ description }}
        <span class="font-semibold">{{ descriptionValue }}</span>
      </p>
    </template>
    <Input
      v-if="confirmValue"
      v-model="confirmInput"
      type="text"
      :placeholder="confirmPlaceholder"
    />
  </Dialog>
</template>
